import React from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/browser";
import { navigate } from "wouter/use-browser-location";
import { login } from "./Auth/authApi";
import { getProfile, getTenantProfile } from "./api";
import { continueMcpOAuth } from "./Auth/mcpOAuthReturn";
import { takeReturnTo } from "./Auth/returnTo";

export const NBoilerplateContext = React.createContext(null);

// mixpanel-browser sets __loaded after init(); calling identify / people.set
// before init throws (people is undefined, internal config missing).
const isMixpanelReady = () => Boolean(mixpanel && mixpanel.__loaded);

// Sentry.getClient() is undefined until Sentry.init() runs — in v7+ and v8+
// alike. Wrap the access in case the export shape varies across versions.
const isSentryReady = () => {
  try {
    return typeof Sentry?.getClient === "function" && Sentry.getClient() != null;
  } catch {
    return false;
  }
};

export function createStore({ name = "nboiler", appState }) {
  return create(
    persist(
      immer((set, get) => ({
        authToken: null,
        // setAuthToken: (token) => set(() => ({ authToken: token })),
        setAuthToken: async (token) => {
          // First, update the authToken in the state
          set(() => ({ authToken: token }));

          if (token && token.length > 0) {
            try {
              const userProfile = await getProfile(token);
              get().setProfile(userProfile);
              const tenantProfile = await getTenantProfile(token);
              get().setTenantProfile(tenantProfile);
            } catch (error) {
              console.error(
                "Error fetching profiles after auth token change:",
                error
              );
              // If fetching profiles fails, clear them and potentially the token
              get().setTenantProfile(null);
              get().setProfile(null); // This will also reset Sentry/Mixpanel
              // Optionally, if a profile fetch error implies the token is invalid, clear it:
              // set(() => ({ authToken: null }));
            }
          } else {
            // If token is falsy (e.g., null, undefined), clear profiles
            get().setTenantProfile(null);
            get().setProfile(null); // This will also reset Sentry/Mixpanel
          }
        },
        authName: null,
        setAuthName: (name) => set(() => ({ authName: name })),
        // True after a password login when the tenant requires passkey sign-in but this user has
        // none yet — the app shell gates everything behind forced passkey enrollment until cleared.
        mustEnrollPasskey: false,
        clearMustEnrollPasskey: () => set(() => ({ mustEnrollPasskey: false })),
        imports: null,
        tenantProfile: null,
        setTenantProfile: (tenantProfile) => set(() => ({ tenantProfile })),
        profile: null,
        setProfile: (profile) => {
          if (profile) {
            console.log("Setting profile", profile);
            if (isSentryReady()) {
              try { Sentry.setUser({ userId: profile.id }); }
              catch (err) { console.error("Sentry.setUser failed:", err); }
            }
            if (isMixpanelReady()) {
              try {
                mixpanel.identify(profile.id);
                mixpanel.people?.set({
                  $name: `${profile.firstName} ${profile.lastName}`,
                  $email: profile.emailAddress,
                  tenantId: profile.tenantId,
                  role: profile.role,
                  // TBD: plan?!
                });
              } catch (err) {
                console.error("Mixpanel identify failed:", err);
              }
            }
          } else {
            if (isMixpanelReady()) {
              try { mixpanel.reset(); }
              catch (err) { console.error("Mixpanel reset failed:", err); }
            }
            if (isSentryReady()) {
              try { Sentry.setUser(null); }
              catch (err) { console.error("Sentry.setUser(null) failed:", err); }
            }
          }
          set(() => ({ profile }));
        },
        login: async (dto) => {
          // console.log("Logging in with", dto);
          const data = await login(dto);
          // console.log("Login response", data);
          await get().userLoggedIn(data);
        },
        userLoggedIn: async (data) => {
          get().setProfile(null);
          get().setTenantProfile(null);
          await get().setAuthToken(data.access_token);
          // Tenant-mandated passkey: a password login is only allowed as a one-time bridge for a
          // user with no passkey yet. Flag it so the shell forces enrollment before app access.
          set(() => ({ mustEnrollPasskey: !!data.requires_passkey_enrollment }));
          if (continueMcpOAuth(data.access_token)) return;
          const profile = get().profile;
          if (profile) get().setAuthName(profile.emailAddress);
          // A page that sent the visitor to sign in (e.g. a supplier invitation) comes first.
          const returnTo = takeReturnTo();
          if (returnTo && profile) {
            navigate(returnTo, { replace: true });
            return;
          }
          if (profile) {
            if (profile.role === "GlobalAdmin") {
              navigate("/admin/jobs", { replace: true });
              return;
            }
            if (profile.role === "MarketplacePartner") {
              navigate("/partner", { replace: true });
              return;
            }
          }
          console.debug("Navigating to home/dashboard after login");
          navigate("/", { replace: true });
        },
        ...(appState ? appState(set, get) : {}), // Spread the appState slice into the store
      })),
      {
        name: name,
        partialize: (state) => ({
          authToken: state.authToken,
          authName: state.authName,
          profile: state.profile,
          tenantProfile: state.tenantProfile,
          // Persisted on purpose: authToken + profile are persisted, so a page reload keeps the user
          // "logged in". If this gate flag were NOT persisted, a reload would drop it and let an
          // un-enrolled user straight into the app — a bypass. It's cleared on logout and overwritten
          // from the server on every login; ForcedPasskeyEnrollment self-heals if a passkey exists.
          mustEnrollPasskey: state.mustEnrollPasskey,
          ...(appState?.partialize?.(state) || {}),
        }),
      }
    )
  );
}
